import React from 'react';
import {FormattedMessage} from 'react-intl';
import {FlexColumn, Modal, Text} from '@forecast-it/design-system';
import {useRouterPrompt, UseRouterPromptProps} from '../../../shared/hooks/useRouterPrompt';

// `onSave` is optional on the shared hook (a caller with no save path, e.g. the AI report
// builder's leave-without-saving overlay, can omit it), but this component always renders a Save
// button wired to it — so pin it back to required here rather than inheriting the widened type.
// An `onSave`-less caller of this specific component would get a Save button that silently no-ops
// and leaves the blocker armed with no way through.
export type SaveReportPromptProps = UseRouterPromptProps & {onSave: NonNullable<UseRouterPromptProps['onSave']>};
export const SaveReportPrompt = ({
	hasChanges,
	onSave,
	onSaveAs,
	onDiscard,
}: SaveReportPromptProps): React.JSX.Element | null => {
	const {showPrompt, handleSave, handleSaveAs, handleDiscard, closePrompt} = useRouterPrompt({
		hasChanges,
		onSave,
		onSaveAs,
		onDiscard,
		ignoreChildPaths: true,
	});
	return showPrompt ? (
		<Modal onCloseModal={handleDiscard}>
			<Modal.Title>
				<FormattedMessage id={'common.save_changes'} />
			</Modal.Title>
			<Modal.Content>
				<FlexColumn>
					<Text>
						<FormattedMessage id={'common.save_changes_text'} />
					</Text>
				</FlexColumn>
			</Modal.Content>
			<Modal.Footer>
				<Modal.PrimaryFooterButton
					onPress={() => {
						closePrompt();
						handleSave();
					}}
				>
					<FormattedMessage id={'common.save'} />
				</Modal.PrimaryFooterButton>
				{onSaveAs && (
					<Modal.SecondaryFooterButton
						onPress={() => {
							closePrompt();
							handleSaveAs();
						}}
					>
						<FormattedMessage id={'saved_report.save_as'} />
					</Modal.SecondaryFooterButton>
				)}
				<Modal.TertiaryFooterButton onPress={handleDiscard}>
					<FormattedMessage id={'common.discard'} />
				</Modal.TertiaryFooterButton>
			</Modal.Footer>
		</Modal>
	) : null;
};
